import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

interface UserActivity {
  id: number;
  user_name: string;
  action_type: string;
  entity_type: string | null;
  entity_id: number | null;
  entity_name: string | null;
  details: string | null;
  created_at: string;
}

interface SystemLog {
  id: number;
  title: string;
  description: string;
  category: string;
  created_by: string;
  created_at: string;
}

interface ActivityLogProps {
  currentUser: string;
}

const ActivityLog: React.FC<ActivityLogProps> = ({ currentUser }) => {
  const [activeTab, setActiveTab] = useState<'user' | 'system'>('user');
  const [userActivity, setUserActivity] = useState<UserActivity[]>([]);
  const [systemLogs, setSystemLogs] = useState<SystemLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [showSystemLogModal, setShowSystemLogModal] = useState(false);
  const [newSystemLog, setNewSystemLog] = useState({
    title: '',
    description: '',
    category: 'feature'
  });

  // Check if user is admin
  const isAdmin = ['Drew McKenna', 'Mike McKenna', 'Cynde Smith'].includes(currentUser);

  useEffect(() => {
    if (activeTab === 'user') {
      loadUserActivity();
    } else {
      loadSystemLogs();
    }
  }, [activeTab, currentUser]);

  const loadUserActivity = async () => {
    try {
      setLoading(true);
      const userName = isAdmin ? 'all' : currentUser;
      const response = await axios.get(`${API_URL}/api/activity-logs/user/${encodeURIComponent(userName)}`);
      setUserActivity(response.data);
    } catch (error) {
      console.error('Failed to load user activity:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSystemLogs = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/activity-logs/system`);
      setSystemLogs(response.data);
    } catch (error) {
      console.error('Failed to load system logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSystemLog = async () => {
    try {
      await axios.post(`${API_URL}/api/activity-logs/system`, {
        ...newSystemLog,
        created_by: currentUser
      });
      setShowSystemLogModal(false);
      setNewSystemLog({ title: '', description: '', category: 'feature' });
      loadSystemLogs();
    } catch (error) {
      console.error('Failed to create system log:', error);
      alert('Failed to create system log');
    }
  };

  const getActionIcon = (actionType: string) => {
    const icons: { [key: string]: string } = {
      created: '➕',
      assigned: '👤',
      updated: '✏️',
      completed: '✅',
      commented: '💬',
      deleted: '🗑️'
    };
    return icons[actionType] || '📝';
  };

  const getActionColor = (actionType: string) => {
    const colors: { [key: string]: string } = {
      created: '#3b82f6',
      assigned: '#8b5cf6',
      updated: '#f59e0b',
      completed: '#10b981',
      commented: '#06b6d4',
      deleted: '#ef4444'
    };
    return colors[actionType] || '#6b7280';
  };

  const getCategoryBadgeColor = (category: string) => {
    const colors: { [key: string]: string } = {
      feature: 'bg-blue-100 text-blue-800',
      bugfix: 'bg-red-100 text-red-800',
      improvement: 'bg-green-100 text-green-800',
      general: 'bg-gray-100 text-gray-800'
    };
    return colors[category] || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
  };

  return (
    <div style={{ padding: '30px', maxWidth: '1200px', margin: '0 auto' }}>
      <div style={{ marginBottom: '30px' }}>
        <h1 style={{ fontSize: '28px', fontWeight: 'bold', color: '#111827', marginBottom: '8px' }}>
          Activity Log
        </h1>
        <p style={{ color: '#6b7280', fontSize: '14px' }}>
          {isAdmin ? 'View all platform activity and system updates' : 'Track your activity and view system updates'}
        </p>
      </div>

      {/* Tabs */}
      <div style={{ borderBottom: '2px solid #e5e7eb', marginBottom: '24px', display: 'flex', gap: '24px' }}>
        <button
          onClick={() => setActiveTab('user')}
          style={{
            padding: '12px 0',
            fontSize: '15px',
            fontWeight: '600',
            color: activeTab === 'user' ? '#3b82f6' : '#6b7280',
            backgroundColor: 'transparent',
            border: 'none',
            borderBottom: activeTab === 'user' ? '2px solid #3b82f6' : '2px solid transparent',
            marginBottom: '-2px',
            cursor: 'pointer',
            transition: 'all 0.2s'
          }}
        >
          {isAdmin ? 'All Activity' : 'My Activity'}
        </button>
        <button
          onClick={() => setActiveTab('system')}
          style={{
            padding: '12px 0',
            fontSize: '15px',
            fontWeight: '600',
            color: activeTab === 'system' ? '#3b82f6' : '#6b7280',
            backgroundColor: 'transparent',
            border: 'none',
            borderBottom: activeTab === 'system' ? '2px solid #3b82f6' : '2px solid transparent',
            marginBottom: '-2px',
            cursor: 'pointer',
            transition: 'all 0.2s'
          }}
        >
          System Log
        </button>
      </div>

      {/* User Activity Tab */}
      {activeTab === 'user' && (
        <div>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
              Loading activity...
            </div>
          ) : userActivity.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '60px 20px',
              backgroundColor: '#f9fafb',
              borderRadius: '8px',
              border: '1px solid #e5e7eb'
            }}>
              <p style={{ fontSize: '16px', color: '#6b7280', marginBottom: '8px' }}>No activity yet</p>
              <p style={{ fontSize: '14px', color: '#9ca3af' }}>
                {isAdmin ? 'No user activity has been logged yet.' : 'Start working on tasks to see your activity here.'}
              </p>
            </div>
          ) : (
            <div style={{ position: 'relative' }}>
              {/* Timeline line */}
              <div style={{
                position: 'absolute',
                left: '20px',
                top: '20px',
                bottom: '20px',
                width: '2px',
                backgroundColor: '#e5e7eb'
              }} />

              {userActivity.map((activity) => (
                <div
                  key={activity.id}
                  style={{
                    position: 'relative',
                    paddingLeft: '60px',
                    marginBottom: '24px'
                  }}
                >
                  {/* Timeline dot */}
                  <div style={{
                    position: 'absolute',
                    left: '12px',
                    top: '8px',
                    width: '18px',
                    height: '18px',
                    borderRadius: '50%',
                    backgroundColor: 'white',
                    border: `3px solid ${getActionColor(activity.action_type)}`,
                    zIndex: 1
                  }} />

                  {/* Activity card */}
                  <div style={{
                    backgroundColor: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    padding: '16px',
                    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'start', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '18px' }}>{getActionIcon(activity.action_type)}</span>
                        <span style={{ fontWeight: '600', color: '#111827', fontSize: '14px' }}>
                          {activity.user_name}
                        </span>
                        <span style={{ color: '#6b7280', fontSize: '14px' }}>
                          {activity.action_type}
                        </span>
                        {activity.entity_type && (
                          <>
                            <span style={{ color: '#6b7280', fontSize: '14px' }}>a</span>
                            <span style={{ color: '#3b82f6', fontSize: '14px', fontWeight: '500' }}>
                              {activity.entity_type}
                            </span>
                          </>
                        )}
                      </div>
                      <span style={{ fontSize: '12px', color: '#9ca3af', whiteSpace: 'nowrap' }}>
                        {formatDate(activity.created_at)}
                      </span>
                    </div>

                    {activity.entity_name && (
                      <div style={{
                        fontSize: '14px',
                        color: '#374151',
                        marginBottom: '4px',
                        fontWeight: '500'
                      }}>
                        {activity.entity_name}
                      </div>
                    )}

                    {activity.details && (
                      <div style={{
                        fontSize: '13px',
                        color: '#6b7280',
                        fontStyle: 'italic'
                      }}>
                        {activity.details}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* System Log Tab */}
      {activeTab === 'system' && (
        <div>
          {isAdmin && (
            <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowSystemLogModal(true)}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                + Add System Log
              </button>
            </div>
          )}

          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
              Loading system logs...
            </div>
          ) : systemLogs.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '60px 20px',
              backgroundColor: '#f9fafb',
              borderRadius: '8px',
              border: '1px solid #e5e7eb'
            }}>
              <p style={{ fontSize: '16px', color: '#6b7280', marginBottom: '8px' }}>No system logs yet</p>
              <p style={{ fontSize: '14px', color: '#9ca3af' }}>
                Platform updates and changes will appear here.
              </p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {systemLogs.map((log) => (
                <div
                  key={log.id}
                  style={{
                    backgroundColor: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    padding: '20px',
                    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'start', justifyContent: 'space-between', marginBottom: '12px' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                        <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#111827', margin: 0 }}>
                          {log.title}
                        </h3>
                        <span className={getCategoryBadgeColor(log.category)} style={{
                          padding: '2px 8px',
                          borderRadius: '4px',
                          fontSize: '11px',
                          fontWeight: '600',
                          textTransform: 'uppercase'
                        }}>
                          {log.category}
                        </span>
                      </div>
                      <p style={{ fontSize: '14px', color: '#6b7280', margin: 0, lineHeight: '1.6' }}>
                        {log.description}
                      </p>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px', fontSize: '12px', color: '#9ca3af' }}>
                    <span>By {log.created_by}</span>
                    <span>•</span>
                    <span>{formatDate(log.created_at)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Add System Log Modal */}
      {showSystemLogModal && (
        <div className="modal-backdrop">
          <div className="modal-content" style={{ maxWidth: '600px' }}>
            <h2 className="modal-title">Add System Log</h2>

            <div className="form-group">
              <label className="form-label">Title *</label>
              <input
                type="text"
                value={newSystemLog.title}
                onChange={(e) => setNewSystemLog({ ...newSystemLog, title: e.target.value })}
                className="form-input"
                placeholder="e.g., New Activity Log Feature"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Description *</label>
              <textarea
                value={newSystemLog.description}
                onChange={(e) => setNewSystemLog({ ...newSystemLog, description: e.target.value })}
                className="form-textarea"
                rows={4}
                placeholder="Describe what changed or was added..."
              />
            </div>

            <div className="form-group">
              <label className="form-label">Category</label>
              <select
                value={newSystemLog.category}
                onChange={(e) => setNewSystemLog({ ...newSystemLog, category: e.target.value })}
                className="form-input"
              >
                <option value="feature">Feature</option>
                <option value="bugfix">Bug Fix</option>
                <option value="improvement">Improvement</option>
                <option value="general">General</option>
              </select>
            </div>

            <div className="modal-actions">
              <button
                onClick={() => {
                  setShowSystemLogModal(false);
                  setNewSystemLog({ title: '', description: '', category: 'feature' });
                }}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateSystemLog}
                className="btn btn-primary"
                disabled={!newSystemLog.title || !newSystemLog.description}
              >
                Create Log
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActivityLog;
